import { useTranslation } from '../../../i18n/useTranslation';
import type { OverviewCandidate } from '../../../services/livestockOverviewService';
import './LivestockOverview.css';

export interface OverviewPhotoMarkersProps {
  imageUrl: string;
  candidates: Array<Pick<OverviewCandidate, 'id' | 'position' | 'box'>>;
  /** Ids of the selected candidates (unselected boxes are drawn dashed). */
  selectedIds: ReadonlySet<string>;
}

const pct = (value: number): string => `${Math.max(0, Math.min(100, value * 100))}%`;

/**
 * The overview photo (as the AI saw it) with a numbered box per found animal. The boxes
 * are decorative (aria-hidden): the list below is the accessible UI; the image's text
 * alternative says what the numbers mean.
 */
export function OverviewPhotoMarkers({ imageUrl, candidates, selectedIds }: OverviewPhotoMarkersProps) {
  const { t } = useTranslation();
  return (
    <figure className="livestock-overview__figure">
      <div className="livestock-overview__photo-frame">
        <img className="livestock-overview__photo" src={imageUrl} alt={t('livestockOverview.review.photoAlt')} />
        {candidates.map((candidate) => (
          <span
            key={candidate.id}
            aria-hidden="true"
            data-testid="overview-marker"
            className={`livestock-overview__marker${selectedIds.has(candidate.id) ? '' : ' livestock-overview__marker--off'}`}
            style={{
              left: pct(candidate.box.x),
              top: pct(candidate.box.y),
              width: pct(candidate.box.width),
              height: pct(candidate.box.height),
            }}
          >
            <span className="livestock-overview__marker-label">{candidate.position}</span>
          </span>
        ))}
      </div>
    </figure>
  );
}

export default OverviewPhotoMarkers;
